import AddOutlined from '@mui/icons-material/AddOutlined';
import Check from '@mui/icons-material/Check';
import CropRotateOutlined from '@mui/icons-material/CropRotateOutlined';
import RemoveOutlined from '@mui/icons-material/RemoveOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/system/Box';
import * as Sentry from '@sentry/react';
import {
  getV1CompaniesCompanyIdTicketsId,
  postV1CompaniesCompanyIdTicketsTicketIdImage,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TransformComponent, TransformWrapper, useControls } from 'react-zoom-pan-pinch';

import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import { useDirectUploads } from '~hooks/useDirectUploads/useDirectUploads';
import useEffectiveCompanyId from '~hooks/useEffectiveCompanyId/useEffectiveCompanyId';
import useImageManipulator from '~hooks/useImageManipulator/useImageManipulator';
import { Ticket } from '~hooks/useTickets/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { ATTACHMENT_IMAGE_CROSS_ORIGIN } from '~utils/image/attachmentImageCrossOrigin';
import { describeSignedUrlLifetime } from '~utils/tickets/signedUrlLifetime';

import { FlexColumn, FlexRow } from '../styled';
import { SmallIconButton } from './styled';

const ROTATED_IMAGE_EXPORT_QUALITY = 0.9;

interface TicketImageProps {
  imageUrl: string;
  imageMimeType: string;
  isDisabled?: boolean;
  onTicketImageStateChange: (isManipulatinImage: boolean) => void;
  refreshTicket: (ticket: Ticket) => void;
  ticketId: string;
}

const isPdfFile = (mimeType: string): boolean => {
  return mimeType === 'application/pdf';
};

// Everything past the origin carries the signed token that grants access to the image,
// so only the origin is safe to report.
const describeTicketImageSource = (ticketId: string, imageUrl: string) => {
  if (imageUrl.startsWith('data:')) {
    return { source: 'rotated_data_url', sourceOrigin: null, ticketId };
  }

  try {
    return { source: 'remote', sourceOrigin: new URL(imageUrl).origin, ticketId };
  } catch {
    return { source: 'unparseable', sourceOrigin: null, ticketId };
  }
};

export default function TicketImage({
  imageUrl: initialImageUrl,
  imageMimeType,
  isDisabled,
  onTicketImageStateChange,
  refreshTicket,
  ticketId,
}: TicketImageProps) {
  const { toasterStore } = useStores();
  const companyId = useEffectiveCompanyId();
  const { loadImage, rotateAndExportImage } = useImageManipulator();
  const { createDirectUpload } = useDirectUploads();

  const [imageUrl, setImageUrl] = useState(initialImageUrl);
  const isPdf = isPdfFile(imageMimeType);
  const [rotatedImage, setRotatedImage] = useState<{
    file?: File;
    isPendingUpload: boolean;
    isRotating: boolean;
  }>({ isPendingUpload: false, isRotating: false });
  const [isUploading, setIsUploading] = useState(false);

  const displayedImageRef = useRef<HTMLImageElement>(null);
  const refreshedTicketIdRef = useRef<string | null>(null);
  const [loadedImageUrl, setLoadedImageUrl] = useState<string | null>(null);
  const [hasImageLoadError, setHasImageLoadError] = useState(false);
  const [isRefreshingImageUrl, setIsRefreshingImageUrl] = useState(false);

  const handleUploadClick = useCallback(async () => {
    if (!rotatedImage?.file) {
      return;
    }

    setIsUploading(true);
    onTicketImageStateChange(true);

    try {
      // Upload via DirectUpload and send the resulting signed_id — the endpoint's
      // generated client serializes the JSON body, so a raw File would stringify to
      // `{}` and 422 (`File can't be blank`). See TRE-15769 / CUS-393.
      const directUpload = await createDirectUpload(rotatedImage.file);
      const response = await postV1CompaniesCompanyIdTicketsTicketIdImage({
        path: {
          'company-id': companyId,
          'ticket-id': ticketId,
        },
        body: { image: directUpload.signed_id },
      });

      const updatedTicket = Ticket.parse(response.data.data);

      setRotatedImage({ isPendingUpload: false, isRotating: false });
      refreshTicket(updatedTicket);
      toasterStore.push(alert(t('approvals.ticket_updated'), AlertTypes.success));
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.tickets.failed_to_upload_ticket_image'),
      );
    } finally {
      setIsUploading(false);
      onTicketImageStateChange(false);
    }
  }, [
    companyId,
    ticketId,
    rotatedImage?.file,
    onTicketImageStateChange,
    refreshTicket,
    createDirectUpload,
    toasterStore,
  ]);

  const handleImageLoad = useCallback(() => {
    refreshedTicketIdRef.current = null;
    setLoadedImageUrl(imageUrl);
  }, [imageUrl]);

  const handleImageLoadError = useCallback(async () => {
    const imageSource = describeTicketImageSource(ticketId, imageUrl);
    // The URL is a presigned S3 link that expires while the ticket payload stays cached,
    // so re-read the ticket once for a freshly signed URL before calling it a failure.
    const canRefreshSignedUrl =
      imageSource.source === 'remote' && refreshedTicketIdRef.current !== ticketId;
    let refreshResponseStatus: number | undefined;

    if (canRefreshSignedUrl) {
      refreshedTicketIdRef.current = ticketId;
      setIsRefreshingImageUrl(true);

      try {
        const response = await getV1CompaniesCompanyIdTicketsId({
          path: { 'company-id': companyId, id: ticketId },
        });
        const refreshedTicket = Ticket.parse(response.data.data);

        if (refreshedTicket.imageUrl && refreshedTicket.imageUrl !== imageUrl) {
          refreshTicket(refreshedTicket);
          return;
        }
      } catch (error) {
        refreshResponseStatus = isAxiosError(error) ? error.response?.status : undefined;
      } finally {
        setIsRefreshingImageUrl(false);
      }
    }

    setHasImageLoadError(true);
    Sentry.captureException(new Error('FAILED_TO_LOAD_TICKET_IMAGE'), {
      contexts: {
        ticket_image: {
          ...imageSource,
          ...(imageSource.source === 'remote' ? describeSignedUrlLifetime(imageUrl) : {}),
          ...(refreshResponseStatus === undefined ? {} : { refreshResponseStatus }),
          retried: refreshedTicketIdRef.current === ticketId,
        },
      },
    });
  }, [companyId, imageUrl, refreshTicket, ticketId]);

  const handleRotateClick = useCallback(async () => {
    setRotatedImage((state) => ({
      isPendingUpload: state.isPendingUpload,
      isRotating: true,
    }));
    onTicketImageStateChange(true);

    const displayedImage = displayedImageRef.current;
    const isDisplayedImageReusable =
      displayedImage !== null && loadedImageUrl === imageUrl;

    try {
      const image = isDisplayedImageReusable ? displayedImage : await loadImage(imageUrl);
      const rotatedImage = await rotateAndExportImage(image, {
        exportFileName: ticketId,
        exportFormat: imageMimeType || undefined,
        exportQuality: ROTATED_IMAGE_EXPORT_QUALITY,
      });

      setImageUrl(rotatedImage.url);
      setRotatedImage({
        file: rotatedImage.file,
        isPendingUpload: true,
        isRotating: false,
      });
    } catch (error) {
      setRotatedImage((state) => ({
        isPendingUpload: state.isPendingUpload,
        isRotating: false,
      }));

      Sentry.captureException(error, {
        contexts: {
          ticket_image: {
            ...describeTicketImageSource(ticketId, imageUrl),
            isDisplayedImageReusable,
          },
        },
      });
      toasterStore.push(
        alert(
          t('ticket_drawer_v2.image_manipulation.failed_to_rotate_image_error_message'),
          AlertTypes.error,
        ),
      );
    } finally {
      onTicketImageStateChange(false);
    }
  }, [
    imageMimeType,
    imageUrl,
    loadImage,
    loadedImageUrl,
    onTicketImageStateChange,
    rotateAndExportImage,
    ticketId,
    toasterStore,
  ]);

  useEffect(() => {
    setImageUrl(initialImageUrl);
  }, [initialImageUrl]);

  useEffect(() => {
    setHasImageLoadError(false);
    setLoadedImageUrl(null);
  }, [imageUrl]);

  return (
    <Box
      sx={() => ({
        backgroundImage: `
          repeating-linear-gradient(45deg, #f0f0f0 25%, transparent 25%, transparent 75%, #f0f0f0 75%, #f0f0f0),
          repeating-linear-gradient(45deg, #f0f0f0 25%, white 25%, white 75%, #f0f0f0 75%, #f0f0f0);
        `,
        backgroundPosition: '0 0, 10px 10px',
        backgroundSize: '20px 20px',
        display: 'flex',
        maxHeight: '100%',
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
      })}
    >
      {isRefreshingImageUrl ? (
        <FlexRow sx={{ alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <CircularProgress color="secondary" />
        </FlexRow>
      ) : hasImageLoadError ? (
        <FlexRow sx={{ alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <ShakyErrorIndicator
            message={t('error_messages.tickets.failed_to_load_ticket_image')}
          />
        </FlexRow>
      ) : (
        <TransformWrapper centerOnInit centerZoomedOut limitToBounds>
          <TransformComponent
            contentStyle={{ width: '100%', height: '100%' }}
            wrapperStyle={{ width: '100%', height: '100%' }}
          >
            {isPdf ? (
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <embed
                  // toolbar=0 to hide the pdf viewer toolbar
                  src={imageUrl + '#toolbar=0'}
                  type="application/pdf"
                  style={{
                    border: 'none',
                    width: '100%',
                    height: '100%',
                    flex: 1,
                    pointerEvents: 'none',
                  }}
                />
              </Box>
            ) : (
              <Box
                alt="ticket-preview"
                component="img"
                crossOrigin={ATTACHMENT_IMAGE_CROSS_ORIGIN}
                onError={handleImageLoadError}
                onLoad={handleImageLoad}
                ref={displayedImageRef}
                src={imageUrl}
                sx={{ height: '100%', objectFit: 'contain', width: '100%' }}
              />
            )}
          </TransformComponent>

          <ImageManipulationControls
            isDisabled={isDisabled}
            isPdf={isPdf}
            isRotating={rotatedImage?.isRotating}
            isUploading={isUploading}
            onRotateClick={handleRotateClick}
            onUploadClick={rotatedImage?.isPendingUpload ? handleUploadClick : undefined}
          />
        </TransformWrapper>
      )}
    </Box>
  );
}

interface ImageManipulationControlsProps {
  isDisabled?: boolean;
  isPdf?: boolean;
  isRotating?: boolean;
  isUploading?: boolean;
  onRotateClick?: () => void;
  onUploadClick?: () => void;
}

function ImageManipulationControls({
  isDisabled,
  isPdf,
  isRotating,
  isUploading,
  onRotateClick,
  onUploadClick,
}: ImageManipulationControlsProps) {
  const { zoomIn, zoomOut } = useControls();

  return (
    <FlexColumn sx={{ position: 'absolute', top: 5, right: 5 }}>
      <FlexColumn
        sx={(theme) => ({
          bgcolor: theme.brandV2.colors.treadGray7,
          borderRadius: '50%',
          gap: '1px',
        })}
      >
        <ImageManipulationControlsButton hasLowerSibling onClick={() => zoomIn(1)}>
          <AddOutlined />
        </ImageManipulationControlsButton>

        <ImageManipulationControlsButton hasUpperSibling onClick={() => zoomOut(1)}>
          <RemoveOutlined />
        </ImageManipulationControlsButton>
      </FlexColumn>

      {isDisabled || isPdf ? null : (
        <FlexColumn
          sx={(theme) => ({
            bgcolor: theme.brandV2.colors.treadGray7,
            borderRadius: '50%',
            gap: '1px',
          })}
        >
          <Tooltip
            arrow
            placement="right"
            title={t('ticket_drawer_v2.image_manipulation.rotate_image')}
          >
            <span>
              <ImageManipulationControlsButton
                hasLowerSibling={Boolean(onUploadClick)}
                isDisabled={isRotating || isUploading}
                onClick={onRotateClick}
              >
                {isRotating ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress color="secondary" size={15} />
                  </Box>
                ) : (
                  <CropRotateOutlined />
                )}
              </ImageManipulationControlsButton>
            </span>
          </Tooltip>

          {onUploadClick && (
            <Tooltip
              arrow
              placement="right"
              title={t('ticket_drawer_v2.image_manipulation.upload_rotated_image')}
            >
              <span>
                <ImageManipulationControlsButton
                  hasUpperSibling
                  isDisabled={isRotating || isUploading}
                  onClick={onUploadClick}
                >
                  {isUploading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CircularProgress color="secondary" size={15} />
                    </Box>
                  ) : (
                    <Check />
                  )}
                </ImageManipulationControlsButton>
              </span>
            </Tooltip>
          )}
        </FlexColumn>
      )}
    </FlexColumn>
  );
}

interface ImageManipulationControlsButtonProps {
  hasLowerSibling?: boolean;
  hasUpperSibling?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
}

function ImageManipulationControlsButton({
  children,
  hasLowerSibling,
  hasUpperSibling,
  isDisabled,
  onClick,
}: React.PropsWithChildren<ImageManipulationControlsButtonProps>) {
  return (
    <SmallIconButton
      disabled={isDisabled}
      onClick={onClick}
      sx={{
        bgcolor: 'background.paper',
        '&:hover': { bgcolor: 'background.paper' },
        '&:disabled': {
          bgcolor: 'background.paper',
          cursor: 'not-allowed',
          pointerEvents: 'auto',
        },
        ...(hasLowerSibling ? { borderBottom: '0 !important' } : {}),
        ...(hasUpperSibling ? { borderTop: '0 !important' } : {}),
        ...(hasLowerSibling
          ? {
              borderBottomLeftRadius: '0 !important',
              borderBottomRightRadius: '0 !important',
            }
          : {}),
        ...(hasUpperSibling
          ? { borderTopLeftRadius: '0 !important', borderTopRightRadius: '0 !important' }
          : {}),
      }}
    >
      {children}
    </SmallIconButton>
  );
}
